// Estáticos (§14.2): /static/*.{hash}.json, gerados pelos scripts de §2.7. Com P-06 (09/10): o número de municípios vem
// do cadastro do 2º turno (5.571 no EA12 de 2026), nunca de uma constante; vetor de teste do registry_id em
// fixtures/contract/registry_id/vectors.json.
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidateNumberSchema,
  type ElectionRef,
  ElectionRefSchema,
  type IbgeCode,
  IbgeCodeSchema,
  IntSchema,
  type Iso,
  IsoSchema,
  type Office,
  OfficeSchema,
  type PlaceCode,
  PlaceCodeSchema,
  type RegionCode,
  RegionCodeSchema,
  type SourceRef,
  SourceRefSchema,
  type TseCode,
  TseCodeSchema,
  type UfCode,
  UfCodeSchema,
} from './base';

export interface StaticProvenance {
  /** 'sample' = gerado de fixtures/static/*.sample, sem os arquivos do TSE/IBGE; o build de produção recusa. */
  kind: 'official' | 'sample';
  /** URLs ou nomes dos arquivos de origem (TSE, IBGE, Natural Earth). */
  sources: string[];
  built_at: Iso;
}

export interface Geography {
  schema: 'static.geography';
  schema_version: 1;
  generated_at: Iso;
  sources: SourceRef[];
  provenance: StaticProvenance;
  /** id = registryId(municípios, cidades do exterior); n_municipalities do cadastro do 2º turno (P-06). */
  registry: { id: string; n_municipalities: number; n_abroad: number };
  regions: Array<{ code: RegionCode; name: string; places: PlaceCode[] }>;
  /** 27 UFs + ZZ. */
  places: PlaceInfo[];
  /** Ordem = IBGE numérico crescente; idx = posição. */
  municipalities: MunicipalityInfo[];
  /** Ordem = código TSE numérico crescente; idx = posição. */
  abroad_cities: AbroadCity[];
  /** Ordem = eleitorado decrescente. */
  countries: CountryInfo[];
}
export interface PlaceInfo {
  code: PlaceCode;
  name: string;
  article: 'o' | 'a' | null;
  region: RegionCode;
  ibge_uf: number | null;
  capital_ibge: IbgeCode | null;
  /** Cadastro do 2º turno; na falta, do 1º. */
  electorate_ref: number;
  sections_ref: number;
}
export interface MunicipalityInfo {
  idx: number;
  ibge: IbgeCode;
  tse: TseCode;
  name: string;
  uf: UfCode;
  capital: boolean;
  electorate_ref: number;
  sections_ref: number;
  population: number;
  /** Números das zonas; vazio quando o município tem 1 zona. */
  zones: number[];
  /** No sistema de coordenadas de br-mun.topo.json (§9.2). */
  label_point: [number, number];
}
export interface AbroadCity {
  idx: number;
  tse: TseCode;
  name: string;
  country_iso2: string;
  lat: number;
  lon: number;
  /** IANA, ex.: "Europe/Lisbon". */
  tz: string;
  electorate_ref: number;
  sections_ref: number;
  /** Fechamento das urnas (17h locais) em t_min de Brasília; pode ser < 1020. */
  closes_t_min: number;
  /** No sistema de world.topo.json (§9.12). */
  xy: [number, number];
}
export interface CountryInfo {
  iso2: string;
  name: string;
  n_cities: number;
  electorate_ref: number;
}

export interface CandidateList {
  schema: 'static.candidates';
  schema_version: 1;
  /** Sempre 2026: o ano alargado do acervo (P-26) vale só no cabeçalho do feed. */
  election: ElectionRef<2026>;
  generated_at: Iso;
  sources: SourceRef[];
  provenance: StaticProvenance;
  /** [13, 22] */
  president: [Candidate, Candidate];
  /** Só as UFs com 2º turno, número menor primeiro. */
  governor: Partial<Record<UfCode, [Candidate, Candidate]>>;
}
export interface Candidate {
  number: CandidateNumber;
  office: Office;
  scope: 'BR' | UfCode;
  /** Nome de urna como o TSE publica. */
  ballot_name: string;
  /** Caixa e acentos revisados à mão; ≤ 28 caracteres. */
  display_name: string;
  full_name: string;
  /** Obrigatório (não nulo) no 2º turno — I-10. */
  gender: 'F' | 'M' | null;
  /** Sigla, chave de Party (tema). */
  party_code: string;
  coalition: { name: string; parties: string[] } | null;
  running_mate: string | null;
  /** Sequencial do candidato no TSE (liga foto e DivulgaCandContas). */
  tse_seq: string;
  photo: CandidatePhoto | null;
}
export interface CandidatePhoto {
  /** "/photos/{tse_seq}-96.{hash}.webp" */
  src: string;
  /** "/photos/{tse_seq}-192.{hash}.webp" */
  src_large?: string;
  width: number;
  height: number;
  /** "Foto: TSE / DivulgaCandContas" */
  credit: string;
  source_url: string;
  license: string;
  sha256: string;
  fetched_at: Iso;
}

export interface FirstRoundResults {
  schema: 'static.first_round';
  schema_version: 1;
  generated_at: Iso;
  sources: SourceRef[];
  provenance: StaticProvenance;
  /** round: 1, date: "2026-10-04" */
  election: ElectionRef<2026>;
  president: { national: FirstRoundShares; places: Record<PlaceCode, FirstRoundShares> };
  /**
   * As 27 UFs quando o arquivo do 1º turno trouxer os eleitos; até lá, só as que existem no insumo (as do 2º turno:
   * 6 desde 12/10, FA-02). UF ausente = sem bloco "1º turno" para ela (§14.2.4: some sozinho).
   */
  governor: Partial<Record<UfCode, FirstRoundGovernor>>;
  /** As 27 UFs quando o Senado for arquivado; UF ausente = sem "Eleitos no 1º turno" do Senado. */
  senate: Partial<Record<UfCode, FirstRoundSenate>>;
  /** P2 (§11.7). */
  chamber?: ArchiveHouse;
  /** P2 (§11.7). */
  assembly?: ArchiveHouse;
  /** P2 (§11.6). */
  party_ideology?: { source: SourceRef; classes: Record<string, 'left' | 'center' | 'right'> };
}
/** Fatia de cada candidato do 2º turno entre TODOS os válidos do 1º turno (§7.8). */
export interface FirstRoundShares {
  valid_votes: number;
  votes: Record<CandidateNumber, number>;
  shares: Record<CandidateNumber, number>;
}
export interface FirstRoundPerson {
  number: CandidateNumber;
  display_name: string;
  party_code: string;
  gender: 'F' | 'M';
  votes: number;
  share: number;
  tse_seq: string;
  photo: CandidatePhoto | null;
}
export interface FirstRoundGovernor {
  status: 'elected' | 'runoff';
  elected: FirstRoundPerson | null;
  /** Número menor primeiro. */
  runoff: [FirstRoundPerson, FirstRoundPerson] | null;
}
export interface FirstRoundSenate {
  /** As 2 vagas de 2026, ordem por votos. */
  elected: [FirstRoundPerson, FirstRoundPerson];
  /** Mandato até 2031. */
  staying: Array<{ name: string; party_code: string }>;
}
export interface ArchiveHouse {
  seats: number;
  by_party: Record<string, number>;
  by_uf: Record<UfCode, { seats: number; by_party: Record<string, number>; top: ArchiveDeputy[] }>;
  /** 20 mais votados do Brasil. */
  top: ArchiveDeputy[];
  /** "/static/archive-1t/{house}-index.{hash}.json" */
  index_file: string;
}
export interface ArchiveDeputy {
  number: string;
  name: string;
  party_code: string;
  uf: UfCode;
  votes: number;
  elected: boolean;
  tse_seq: string;
}

export interface Baseline2022 {
  schema: 'static.baseline_2022';
  schema_version: 1;
  generated_at: Iso;
  sources: SourceRef[];
  provenance: StaticProvenance;
  election: { year: 2022; round: 2; date: '2022-10-30' };
  /** 13 (Lula, PT) e 22 (Bolsonaro, PL). */
  candidates: [Baseline2022Candidate, Baseline2022Candidate];
  /** { PT: '13', PL: '22' } */
  party_to_number: Record<string, '13' | '22'>;
  national: BaselineTally;
  places: Record<PlaceCode, BaselineTally>;
  regions: Record<RegionCode, BaselineTally>;
  /** = Geography.registry.id */
  registry_id: string;
  /** Alinhado a Geography.municipalities[idx]. */
  municipal: BaselineColumns;
  /** Alinhado a Geography.abroad_cities[idx]. */
  abroad: BaselineColumns;
  /** Maior |votos_22 − votos_13| municipal (modo Vantagem, §9.4). */
  advantage_max: { national: number; places: Record<UfCode, number> };
}
export interface Baseline2022Candidate {
  number: '13' | '22';
  display_name: string;
  party_code: 'PT' | 'PL';
}
export interface BaselineTally {
  electorate: number;
  turnout: number;
  blank_votes: number;
  null_votes: number;
  valid_votes: number;
  votes: { '13': number; '22': number };
}
/** −1 = sem correspondência (município ou cidade sem dado em 2022, como Boa Esperança do Norte). */
export interface BaselineColumns {
  valid_votes: number[];
  votes_13: number[];
  votes_22: number[];
  turnout: number[];
}

/**
 * Entrada do registry_id (§14.2.1; P-06/L7): IBGE dos municípios em ordem numérica, unidos por ",", depois "|", depois
 * os códigos TSE das cidades do exterior em ordem numérica, unidos por ",". UTF-8 (só dígitos, vírgula e barra).
 * registry_id = 16 primeiros caracteres hex (minúsculos) do SHA-256 dessa cadeia, sem prefixo. Hash em
 * scripts/geo/registry-id.ts; vetores em fixtures/contract/registry_id/vectors.json.
 */
export function registryIdInput(municipalIbge: readonly string[], abroadTse: readonly string[]): string {
  const byNumber = (a: string, b: string) => Number(a) - Number(b);
  return `${[...municipalIbge].sort(byNumber).join(',')}|${[...abroadTse].sort(byNumber).join(',')}`;
}

export const StaticProvenanceSchema = z.object({
  kind: z.enum(['official', 'sample']),
  sources: z.array(z.string()),
  built_at: IsoSchema,
});

const ArticleSchema = z.nullable(z.enum(['o', 'a']));
const XYSchema = z.tuple([z.number(), z.number()]);

/**
 * Um município da geografia. Separado do `GeographySchema` para a validação em pedaços do app (DECISOES §44): o
 * esquema do arquivo inteiro é este mesmo, então as duas validações aceitam e recusam exatamente o mesmo.
 */
export const GeoMunicipalitySchema = z.object({
  idx: IntSchema,
  ibge: IbgeCodeSchema,
  tse: TseCodeSchema,
  name: z.string(),
  uf: UfCodeSchema,
  capital: z.boolean(),
  electorate_ref: IntSchema,
  sections_ref: IntSchema,
  population: IntSchema,
  zones: z.array(IntSchema),
  label_point: XYSchema,
});

/** Uma cidade do Exterior da geografia (ver `GeoMunicipalitySchema`). */
export const GeoAbroadCitySchema = z.object({
  idx: IntSchema,
  tse: TseCodeSchema,
  name: z.string(),
  country_iso2: z.string(),
  lat: z.number(),
  lon: z.number(),
  tz: z.string(),
  electorate_ref: IntSchema,
  sections_ref: IntSchema,
  closes_t_min: z.number(),
  xy: XYSchema,
});

/** Tudo da geografia antes das duas listas longas (municípios e cidades do Exterior); `countries` vem depois delas. */
const geographyHead = {
  schema: z.literal('static.geography'),
  schema_version: z.literal(1),
  generated_at: IsoSchema,
  sources: z.array(SourceRefSchema),
  provenance: StaticProvenanceSchema,
  registry: z.object({ id: z.string(), n_municipalities: IntSchema, n_abroad: IntSchema }),
  regions: z.array(z.object({ code: RegionCodeSchema, name: z.string(), places: z.array(PlaceCodeSchema) })),
  places: z.array(
    z.object({
      code: PlaceCodeSchema,
      name: z.string(),
      article: ArticleSchema,
      region: RegionCodeSchema,
      ibge_uf: z.nullable(IntSchema),
      capital_ibge: z.nullable(IbgeCodeSchema),
      electorate_ref: IntSchema,
      sections_ref: IntSchema,
    }),
  ),
};

const CountriesSchema = z.array(
  z.object({ iso2: z.string(), name: z.string(), n_cities: IntSchema, electorate_ref: IntSchema }),
);

export const GeographySchema = z.object({
  ...geographyHead,
  municipalities: z.array(GeoMunicipalitySchema),
  abroad_cities: z.array(GeoAbroadCitySchema),
  countries: CountriesSchema,
});

/** O cabeçalho validado e as duas listas só como listas: os itens são validados em pedaços (DECISOES §44). */
export const GeographyHeadSchema = z.object({
  ...geographyHead,
  municipalities: z.array(z.unknown()),
  abroad_cities: z.array(z.unknown()),
  countries: CountriesSchema,
});

export const CandidatePhotoSchema = z.object({
  src: z.string(),
  src_large: z.optional(z.string()),
  width: IntSchema,
  height: IntSchema,
  credit: z.string(),
  source_url: z.string(),
  license: z.string(),
  sha256: z.string(),
  fetched_at: IsoSchema,
});

export const CandidateSchema = z.object({
  number: CandidateNumberSchema,
  office: OfficeSchema,
  scope: z.union([z.literal('BR'), UfCodeSchema]),
  ballot_name: z.string(),
  display_name: z.string(),
  full_name: z.string(),
  gender: z.nullable(z.enum(['F', 'M'])),
  party_code: z.string(),
  coalition: z.nullable(z.object({ name: z.string(), parties: z.array(z.string()) })),
  running_mate: z.nullable(z.string()),
  tse_seq: z.string(),
  photo: z.nullable(CandidatePhotoSchema),
});

export const CandidateListSchema = z.object({
  schema: z.literal('static.candidates'),
  schema_version: z.literal(1),
  election: ElectionRefSchema,
  generated_at: IsoSchema,
  sources: z.array(SourceRefSchema),
  provenance: StaticProvenanceSchema,
  president: z.tuple([CandidateSchema, CandidateSchema]),
  governor: z.partialRecord(UfCodeSchema, z.tuple([CandidateSchema, CandidateSchema])),
});

const FirstRoundSharesSchema = z.object({
  valid_votes: IntSchema,
  votes: z.record(CandidateNumberSchema, IntSchema),
  shares: z.record(CandidateNumberSchema, z.number()),
});
const FirstRoundPersonSchema = z.object({
  number: CandidateNumberSchema,
  display_name: z.string(),
  party_code: z.string(),
  gender: z.enum(['F', 'M']),
  votes: IntSchema,
  share: z.number(),
  tse_seq: z.string(),
  photo: z.nullable(CandidatePhotoSchema),
});
const ArchiveDeputySchema = z.object({
  number: z.string(),
  name: z.string(),
  party_code: z.string(),
  uf: UfCodeSchema,
  votes: IntSchema,
  elected: z.boolean(),
  tse_seq: z.string(),
});
const ArchiveHouseSchema = z.object({
  seats: IntSchema,
  by_party: z.record(z.string(), IntSchema),
  by_uf: z.record(
    UfCodeSchema,
    z.object({ seats: IntSchema, by_party: z.record(z.string(), IntSchema), top: z.array(ArchiveDeputySchema) }),
  ),
  top: z.array(ArchiveDeputySchema),
  index_file: z.string(),
});

export const FirstRoundResultsSchema = z.object({
  schema: z.literal('static.first_round'),
  schema_version: z.literal(1),
  generated_at: IsoSchema,
  sources: z.array(SourceRefSchema),
  provenance: StaticProvenanceSchema,
  election: ElectionRefSchema,
  president: z.object({
    national: FirstRoundSharesSchema,
    places: z.record(PlaceCodeSchema, FirstRoundSharesSchema),
  }),
  governor: z.partialRecord(
    UfCodeSchema,
    z.object({
      status: z.enum(['elected', 'runoff']),
      elected: z.nullable(FirstRoundPersonSchema),
      runoff: z.nullable(z.tuple([FirstRoundPersonSchema, FirstRoundPersonSchema])),
    }),
  ),
  senate: z.partialRecord(
    UfCodeSchema,
    z.object({
      elected: z.tuple([FirstRoundPersonSchema, FirstRoundPersonSchema]),
      staying: z.array(z.object({ name: z.string(), party_code: z.string() })),
    }),
  ),
  chamber: z.optional(ArchiveHouseSchema),
  assembly: z.optional(ArchiveHouseSchema),
  party_ideology: z.optional(
    z.object({ source: SourceRefSchema, classes: z.record(z.string(), z.enum(['left', 'center', 'right'])) }),
  ),
});

const BaselineCandidateSchema = z.object({
  number: z.enum(['13', '22']),
  display_name: z.string(),
  party_code: z.enum(['PT', 'PL']),
});
const BaselineTallySchema = z.object({
  electorate: IntSchema,
  turnout: IntSchema,
  blank_votes: IntSchema,
  null_votes: IntSchema,
  valid_votes: IntSchema,
  votes: z.object({ '13': IntSchema, '22': IntSchema }),
});
const BaselineColumnsSchema = z.object({
  valid_votes: z.array(IntSchema),
  votes_13: z.array(IntSchema),
  votes_22: z.array(IntSchema),
  turnout: z.array(IntSchema),
});

export const Baseline2022Schema = z.object({
  schema: z.literal('static.baseline_2022'),
  schema_version: z.literal(1),
  generated_at: IsoSchema,
  sources: z.array(SourceRefSchema),
  provenance: StaticProvenanceSchema,
  election: z.object({ year: z.literal(2022), round: z.literal(2), date: z.literal('2022-10-30') }),
  candidates: z.tuple([BaselineCandidateSchema, BaselineCandidateSchema]),
  party_to_number: z.record(z.string(), z.enum(['13', '22'])),
  national: BaselineTallySchema,
  places: z.record(PlaceCodeSchema, BaselineTallySchema),
  regions: z.record(RegionCodeSchema, BaselineTallySchema),
  registry_id: z.string(),
  municipal: BaselineColumnsSchema,
  abroad: BaselineColumnsSchema,
  advantage_max: z.object({ national: IntSchema, places: z.record(UfCodeSchema, IntSchema) }),
});
